import { Theme, ThemeConsumer } from '@rneui/themed';
import React, { useState, useEffect, useRef } from 'react';
import { View, TouchableOpacity, StyleSheet, useWindowDimensions, TextInput, ActivityIndicator, ScrollView, Keyboard, Pressable, Text } from 'react-native';
import { ZenDivider, ZenDropdown, ZenIcon, ZenText, ZenToast } from 'zen-core-elements';
import SellscreenModal from '../sellscreenModal';
import { CustomSearchBar } from '../customSearchBar';
import { addCustomer, addParkSale, checkCustomerPhoneNumber, clearCart } from '../../../redux/slices/newSellscreenSlice';
import { config } from '../../../shared/environment';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { createCustomer, getOneCustomerDetails, clearOneCustomerDetails } from '../../../redux/slices/customerSlice';
import { Code, CustomerDetailsProps } from '../../customers/addEditCustomerModule';
import CommonService from '../../../shared/services/commonService';
import { initialStateType } from '../../../redux/types/sellScreenTypes';
import { checkForStoreCreditPayment } from '../../../redux/slices/newSellscreenPaymentSlice';
import { useAppNavigation } from '../../../types/@react-navigation/navigation';
import { CustomModal } from '../../../shared/commonComponents/customModal';
import SwitchRemoveCustomerModal, { SellScreenParkRemoveOptions } from './switchRemoveCustomerModal';


const ActionRow = ({ icon, title, sub, isDestructive, onPress }: { icon: string, title: string, sub: string, isDestructive?: boolean, onPress: () => void }) => (
	<ThemeConsumer>
		{({ theme }) => {
			const styles = style(theme)
			return (
				<TouchableOpacity style={styles.actionRow} onPress={onPress}>
					<View style={[styles.iconBox, { backgroundColor: isDestructive ? '#FFEBEE' : '#EFF4FF' }]}>
						<ZenIcon type='material-community' name={icon} size={20} color={isDestructive ? '#F44336' : '#4A90E2'} />
					</View>
					<View style={{ flex: 1, marginLeft: 12 }}>
						<ZenText content={title} color={isDestructive ? "#F44336" : "#1A1C1E"} bold size={theme.tinyFontSize} />
						<ZenText content={sub} style={styles.actionSub} color='#909090' size={theme.errorFontSize} />
					</View>
					<ZenIcon type='material-community' name="chevron-right" size={20} color="#CCC" />
				</TouchableOpacity>
			)
		}}
	</ThemeConsumer>
);

const CustomerModal = ({ isPaymentScreen = false, disabled = false, isWrite }: { isPaymentScreen?: boolean, disabled?: boolean, isWrite: boolean }) => {
	const { width, height } = useWindowDimensions()

	const dispatch = useAppDispatch()
	const navigation = useAppNavigation()
	const getCountry = useAppSelector((state) => state?.common?.getCountry)
	const { customerDetails: saleCustomer, sellScreenType } = useAppSelector((state) => state?.NewSellscreenSlice) || {}
	const { customerDetails: rawCustomerDetails } = useAppSelector((state) => state.customer?.oneCustomerDetails!) || {}
	const customerDetails = rawCustomerDetails ? {
		id: rawCustomerDetails.user?.userId,
		countryCode: rawCustomerDetails.user?.countryCode,
		loyaltyPoints: Number(rawCustomerDetails.balancePoints) || 0,
		onAccountDetails: { limit: Number(rawCustomerDetails.accountLimit) || 0, balance: Number(rawCustomerDetails.accountLimitValue) || 0 },
		name: rawCustomerDetails.user?.firstName + (rawCustomerDetails.user?.lastName ? (" " + rawCustomerDetails.user?.lastName) : ""),
		phone: rawCustomerDetails.user?.phone?.toString() || "",
		address: null,
		customerId: rawCustomerDetails.user?.userId,
		dateOfBirth: rawCustomerDetails.user?.dateOfBirth,
		email: rawCustomerDetails.user?.email ?? "",
		firstName: rawCustomerDetails.user?.firstName,
		lastName: rawCustomerDetails.user?.lastName ?? "",
		isApproved: rawCustomerDetails.isApproved ?? false,
		isSmsVerified: rawCustomerDetails.user?.isSmsVerified,
		isSocialUser: false,
		isWholesale: rawCustomerDetails.isWholesale,
		isUserVerified: rawCustomerDetails.isApproved ?? false,
		role: { code: rawCustomerDetails.roles?.roleCode || "", role: rawCustomerDetails.roles?.roleName || "" },
		roleId: 636,
		shopappUser: null,
		storeId: config?.storeId
	} : null;
	const { transactionData, isSplitPayment } = useAppSelector((state) => state?.NewSellscreenPaymentSlice)
	const currencySymbol = useAppSelector((state) => state?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol) ?? '₹'
	const [phone, setPhone] = useState<null | string>('');
	const [customerName, setCustomerName] = useState<null | string>('');
	const [foundCustomer, setFoundCustomer] = useState<initialStateType["customerDetails"] | null>(null);
	const [isSearching, setIsSearching] = useState(false);
	const [customerModal, setCustomerModal] = useState(false)
	const [addCustomerLoader, setAddCustomerLoader] = useState(false)
	const [countryCodeValue, setCountryCodeValue] = useState(91)
	//variable to store country code
	const [countryCode, setCountryCode] = useState<Code[]>([])
	const [dropdownOpen, setDropdownOpen] = useState(false)
	const inputRef = useRef<TextInput>(null);
	const [selectedOption, setSelectedOption] = useState<SellScreenParkRemoveOptions | null>(null);
	const [tempNote, setTempNote] = useState('');
	const [parkSaleModalVisible, setParkSaleModalVisible] = useState(false);
	const [isVisible, setIsVisible] = useState(false);
	const [popoverPos, setPopoverPos] = useState({ top: 0, right: 20 });
	const anchorRef = useRef<View | null>(null);
	const [isSwitchRemoveModalVisible, setIsSwitchRemoveModalVisible] = useState({ parkSale: false, removeCustomer: false });
	const { cartProducts } = useAppSelector((state) => state?.NewSellscreenSlice)
	const { id: locationId, name, timeZone } = useAppSelector((state) => state?.common?.currentLocation)

	const togglePopover = () => {
		anchorRef?.current?.measure((x: number, y: number, anchorWidth: number, anchorHeight: number, pageX: number, pageY: number) => {
			setPopoverPos({
				top: pageY + anchorHeight + 10,
				// Aligning the right edge of the popover with the right edge of the trigger
				// (current window width - a module-level value goes stale after a resize/rotation)
				right: width - (pageX + anchorWidth),
			});
			setIsVisible(prev => !prev);
		});
	};

	const StatRow = ({ name, label, value, color, iconBg, iconColor }: {
		name: string,
		label: string,
		value: string,
		color?: string,
		iconBg: string,
		iconColor: string
	}) => (
		<ThemeConsumer>
			{({ theme }) => {
				const styles = style(theme)
				return (
					<View style={styles.statRow}>
						<View style={styles.statLabelContainer}>
							<View style={[styles.iconBox, { backgroundColor: iconBg }]}>
								<ZenIcon type='material-community' name={name} size={18} color={iconColor} />
							</View>
							<ZenText content={label} style={{ marginLeft: 14 }} size={theme.tinyFontSize} color='#444' />
						</View>
						<ZenText bold size={theme.tinyFontSize} content={value} color={color || '#1A1C1E'} />
					</View>
				)
			}}
		</ThemeConsumer>
	)

	useEffect(() => {
		if (saleCustomer) {
			setFoundCustomer(saleCustomer)
			setPhone(saleCustomer?.phone?.toString() || "")
			setCountryCodeValue(saleCustomer?.countryCode)
			setCustomerName(saleCustomer?.name)
		}
	}, [customerModal])

	useEffect(() => {
		if (saleCustomer == null) {
			setFoundCustomer(null)
			setPhone(null)
			setCountryCodeValue(91)
			setCustomerName(null)
		}
	}, [saleCustomer])

	useEffect(() => {
		if (getCountry?.value) {
			let code: Code[] = []
			getCountry?.value?.country?.map((countries) => {
				code?.push({ label: `+${countries?.countryCode} ${countries?.name}`, value: countries?.countryCode })
			})
			setCountryCode(code)
		}
	}, [getCountry])

	useEffect(() => {
		if (phone?.length && (phone !== customerDetails?.phone?.toString() || countryCodeValue !== customerDetails?.countryCode)) {
			setIsSearching(true)
			const debounceSearchText = setTimeout(() => {
				checkPhoneNumber()
			}, 1000);
			return () => { clearTimeout(debounceSearchText) }
		} else if (!phone?.length) {
			setFoundCustomer(null)
			setCustomerName(null)
			setIsSearching(false)
		}
		else if (phone == customerDetails?.phone?.toString()) {
			setFoundCustomer(customerDetails)
			setIsSearching(false)
		}
	}, [phone, countryCodeValue]);

	const checkPhoneNumber = () => {
		dispatch(checkCustomerPhoneNumber({ storeId: config?.storeId, payload: { contactNumber: Number(phone)!!, countryCode: countryCodeValue } })).unwrap().then((response) => {
			if (response?.customerDetails?.isExist) {
				getCustomerDetails(response?.customerDetails?.id)
			} else {
				setFoundCustomer(null)
				setIsSearching(false)
				setAddCustomerLoader(false)
			}
		}).catch(() => {
			ZenToast({ type: "error", message: "Failed to check Phone Number" })
			setIsSearching(false)
			setAddCustomerLoader(false)
		})
	}
	const getCustomerDetails = (customerId: number, isSave?: boolean) => {
		dispatch(getOneCustomerDetails({ storeId: config?.storeId, customerId })).unwrap().then((response) => {
			console.log(JSON.stringify(response, undefined, 4));

			if (response && response?.customerDetails) {
				const { user: { firstName, lastName, countryCode, phone, userId, dateOfBirth = null, email, isSmsVerified }, accountLimitValue, balancePoints, accountLimit, isApproved, isWholesale, roles } = response?.customerDetails
				let customer = {
					id: userId,
					countryCode,
					loyaltyPoints: Number(balancePoints) || 0,
					onAccountDetails: { limit: Number(accountLimit) || 0, balance: Number(accountLimitValue) || 0 },
					name: firstName + (lastName ? (" " + lastName) : ""),
					phone: phone?.toString() || "",
					address: null,
					customerId,
					dateOfBirth,
					email: email ?? "",
					firstName,
					lastName: lastName ?? "",
					isApproved: isApproved ?? false,
					isSmsVerified,
					isSocialUser: false,
					isWholesale,
					isUserVerified: isApproved ?? false,
					role: { code: roles?.roleCode || "", role: roles?.roleName || "" },
					roleId: 636,
					shopappUser: null,
					storeId: config?.storeId
				}
				setFoundCustomer(customer)
				if (isSave) {
					dispatch(addCustomer({
						customerDetails: {
							details: customer, locationId: config?.storeId
						}
					}))
					setCustomerModal(false)
					ZenToast({ type: "success", message: "Customer created and added successfully" })
					setAddCustomerLoader(false)
				}
				setCustomerName(null)
				setIsSearching(false)
			}
		}).catch(() => {
			ZenToast({ type: "error", message: "Failed to get Customer Details" })
			setIsSearching(false)
			setAddCustomerLoader(false)
		})
	}

	// Mock selection for demo
	const handlePress = () => {
		setCustomerModal(true)
	};

	const createAndSelectCustomer = () => {
		if (!isWrite) {
			ZenToast({ message: "You don't have access!", type: "warning" })
			return
		}
		setAddCustomerLoader(true)
		const data: CustomerDetailsProps = {
			"contactInfo": [],
			"customerEntry": {
				'medium': "POS",
				"acceptsMarketing": true,
				"customerCode": "",
				"isApproved": null,
				"isLoyalty": true,
				"isWholesale": false,
				"storeName": null,
				"taxIdentificationNumber": null,
				"customerNotes": null,
				"websiteAddress": null,
				"isAllowManualOrder": false,
			},
			"userInfo": {
				"contactNumber": phone || null,
				"countryCode": countryCodeValue || null,
				"dateOfBirth": null,
				"email": null,
				"firstName": customerName || 'Guest customer',
				"gender": "",
				"lastName": null
			},
			"isEmailMarketing": false
		}
		dispatch(createCustomer({ storeId: config?.storeId, data, isActive: true })).unwrap().then((response) => {
			if (response && response?.createCustomerDetails) {
				getCustomerDetails(response?.id, true)
			}
			else {
				ZenToast({ type: "error", message: "Faild to create and add customer" })
				setAddCustomerLoader(false)
			}
		}).catch((error) => {
			ZenToast({ type: "error", message: "Faild to create and add customer, please try again after some time!!" })
			setAddCustomerLoader(false)
		})
	}

	const handleSelectCustomer = () => {
		//remove
		if (foundCustomer?.id == saleCustomer?.id) {
			dispatch(addCustomer({
				customerDetails: {
					details: null, locationId: config?.storeId
				}
			}))
			setFoundCustomer(null)
			setCustomerName(null)
			setPhone(null)

			if (isSplitPayment && transactionData?.length) {
				dispatch(checkForStoreCreditPayment())
			}
		}
		//add
		else if (foundCustomer) {
			dispatch(addCustomer({
				customerDetails: {
					details: foundCustomer, locationId: config?.storeId
				}
			}))
		}
		setCustomerModal(false)
		setIsVisible(false)
	}

	const onCloseRequest = () => {
		inputRef.current?.blur()
		setCustomerModal(false)
	}

	const captializeFirstLetter = (str: string) => {
		return str?.split(" ")?.map((word) => word?.charAt(0).toUpperCase() + word?.slice(1))?.join(" ")
	}
	const format = (val: number | undefined) => val?.toLocaleString(undefined, { minimumFractionDigits: 0 })!;

	const parkSaleHandler = () => {

	}


	const removeCustomerHandler = () => {
		dispatch(addCustomer({
			customerDetails: {
				details: null, locationId: config?.storeId
			}
		}))
		setFoundCustomer(null)
		setCustomerName(null)
		setPhone(null)

		if (isSplitPayment && transactionData?.length) {
			dispatch(checkForStoreCreditPayment())
		}
	}

	const onSuccess = () => {
		if (selectedOption === "clearCartParkSale") {
			dispatch(clearCart())
			setIsSwitchRemoveModalVisible(prev => ({ ...prev, parkSale: false }))
			setIsVisible(false)
			setSelectedOption(null)
		} else if (selectedOption === "clearCartRemoveCustomer") {
			dispatch(clearCart())
			removeCustomerHandler()
			setIsSwitchRemoveModalVisible(prev => ({ ...prev, removeCustomer: false }))
			setIsVisible(false)
			setSelectedOption(null)
		} else if (selectedOption === "parkSale") {
			setTempNote(saleCustomer?.firstName + "'s Sale")
			setIsSwitchRemoveModalVisible(prev => ({ ...prev, parkSale: false }))
			setParkSaleModalVisible(true)
			setIsVisible(false)
		} else if (selectedOption === "removeCustomer") {
			removeCustomerHandler()
			setIsSwitchRemoveModalVisible(prev => ({ ...prev, removeCustomer: false }))
			setIsVisible(false)
			setSelectedOption(null)
		}
	}

	const handleSave = () => {
		dispatch(addParkSale({
			location: { id: locationId, name, timeZone },
			name: tempNote,
			customerDetails: saleCustomer
		}))
		setTempNote("")
		setParkSaleModalVisible(false);
		setIsVisible(false)
		if (selectedOption) {
			setCustomerModal(true)
		}
		setSelectedOption(null)
	};

	return (
		<ThemeConsumer>
			{({ theme }) => {
				const styles = style(theme)
				return (
					<>
						{
							// isPaymentScreen ?
							// 	<TouchableOpacity
							// 		activeOpacity={0.8}
							// 		disabled={disabled}
							// 		onPress={togglePopover}
							// 		style={styles.customerBox}
							// 	>
							// 		<ZenIcon name="account-circle" type="material-community" size={32} color={theme?.primary} />
							// 		{customerDetails ? <View style={{ marginLeft: 10 }}>
							// 			<ZenText content={customerDetails?.name || "Guest customer"} style={styles.custName} />
							// 			<ZenText content={"+" + customerDetails?.countryCode + " " + customerDetails?.phone} style={styles.custSub} />
							// 		</View> :
							// 			<View style={{ marginLeft: 10 }}>
							// 				<ZenText content="Select Customer" style={styles.custName} />
							// 			</View>
							// 		}
							// 	</TouchableOpacity>
							// 	:
							<View>
								{
									saleCustomer ? <TouchableOpacity
										activeOpacity={0.8}
										disabled={disabled || sellScreenType !== "SELL"}
										ref={anchorRef}
										onPress={togglePopover}
										style={{ borderWidth: 1, borderColor: theme.blue200, borderRadius: 10, flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: 10, paddingHorizontal: 10, paddingVertical: 5, opacity: sellScreenType === "SELL" ? 1 : 0.7 }}
									>
										<View style={{ backgroundColor: theme.blue50, borderRadius: 100, padding: 5, alignItems: "center", justifyContent: "center" }}>
											<ZenIcon name='user' type='feather' size={20} color={theme?.blue600} />
										</View>
										{width > height &&
											<View>
												{saleCustomer?.name && <ZenText bold content={captializeFirstLetter(saleCustomer?.name)} numberOfLines={1} color={theme.primary} size={theme.errorFontSize} />}
												<ZenText content={'+' + saleCustomer?.countryCode + " " + saleCustomer?.phone} numberOfLines={1} size={theme.badgeFontSize} color={theme.primary} />
											</View>
										}
										<View style={{ backgroundColor: theme.blue50, borderRadius: 100, padding: 5, alignItems: "center", justifyContent: "center" }}>
											<ZenIcon name='chevron-down' type='entypo' size={20} color={theme?.blue600} />
										</View>


									</TouchableOpacity > :
										<TouchableOpacity
											activeOpacity={0.8}
											disabled={disabled}
											onPress={handlePress}
											style={[styles.customerChip, styles.inactiveChip]}
										>
											<View style={[styles.iconCircle, styles.inactiveCircle]}>
												<ZenIcon name='user-plus' type='feather' size={14} color={theme?.disabledGray500} />
											</View>

											<ZenText content={'Select Customer'} numberOfLines={1} style={[styles.customerName, saleCustomer ? styles.textActive : styles.textInactive]} />
										</TouchableOpacity >
								}
							</View>
						}
						<SellscreenModal isModalOpen={customerModal} onShow={() => { inputRef.current?.focus() }} onCloseRequest={onCloseRequest}>
							<View style={{ maxWidth: width > 900 ? width * 0.30 : width > 600 ? width * 0.60 : width * 0.80, maxHeight: width > 900 ? height * 0.90 : height * 0.40, }}>
								<View style={styles.header}>
									<ZenText content="Add Customer" style={styles.headerTitle} />
									<TouchableOpacity onPress={() => setCustomerModal(false)}>
										<ZenIcon name='close' type='antdesign' color={theme?.lightDark} size={24} />
									</TouchableOpacity>
								</View>

								<View style={{ paddingTop: 16 }}>

									<>
										<ZenDropdown
											title="Country Code *"
											titleStyle={[styles.label, { paddingBottom: -5 }]}
											items={countryCode as []}
											open={dropdownOpen}
											setOpen={() => { }}
											onPress={() => {
												setDropdownOpen((prev) => !prev)
												Keyboard.dismiss()
											}}
											onClose={() => {
												setDropdownOpen(false)
											}}
											setValue={() => { }}
											value={countryCodeValue}
											onSelectItem={(values) => {
												setCountryCodeValue(Number(values?.value))
												setDropdownOpen(false)
											}}
											searchable={true}
											multiple={false}
											autoScroll={true}
											placeholder={"Select a country code"}
											listItemLabelStyle={{ fontSize: theme?.smallHeaderFontSize }}
											labelStyle={{ fontSize: theme?.smallHeaderFontSize, color: theme?.lightDark, fontFamily: theme?.medium, paddingLeft: 27 }}
											placeholderStyle={{ fontSize: theme?.smallFontSize, color: theme?.labelColor, fontFamily: theme?.medium }}
											style={{ backgroundColor: theme?.iceWhite }}
											dropDownContainerStyle={{ position: "relative", top: 0 }}
											containerStyle={{ marginBottom: 15 }}
										/>
										<ZenIcon name='earth' type='antdesign' size={18} color={theme?.grey} style={{ position: "absolute", top: 50, left: 12 }} />
									</>

									<ZenText content='Phone Number' style={styles.label} />

									<View style={{ paddingBottom: 10 }}>
										<CustomSearchBar
											inputRef={inputRef}
											placeholder="Enter phone number"
											search={phone}
											// Digits only - number-pad does not stop letters on web or hardware keyboards
											setSearch={(text) => setPhone(text == null ? null : text.replace(/\D/g, ''))}
											toggleOffset={() => { }}
											backgroundColor={theme?.iceWhite}
											leftIcon={false}
											border={true}
											onFocus={() => {
												setDropdownOpen(false)
											}}
											keyboardType='number-pad'
											height={50}
											width={"100%"}
											leftComponent={<ZenIcon name='phone' type='feather' size={18} color={theme?.grey} style={{ paddingRight: 5 }} />}
										/>
									</View>

									<View style={styles.dynamicArea}>
										{isSearching ?
											<View style={{ flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 7 }}>
												<ActivityIndicator size="small" color={theme?.primary} />
												<ZenText content="Searching customer details..." style={styles.label} />
											</View>
											:
											foundCustomer ? (
												/* --- EXISTING CUSTOMER CARD --- */
												<View style={styles.customerCard}>
													<View style={styles.cardHeader}>
														<View style={styles.avatar}>
															<ZenText content={foundCustomer?.name[0]} style={styles.avatarText} />
														</View>
														<View style={styles.mainInfo}>
															<ZenText content={foundCustomer?.name} style={styles.customerName} />
															<ZenText content={foundCustomer?.phone} style={styles.customerPhone} />
														</View>
														<TouchableOpacity
															style={[styles.selectBtn, foundCustomer?.id == saleCustomer?.id && { backgroundColor: theme?.red500 }]}
															onPress={handleSelectCustomer}
														>
															<ZenText content={foundCustomer?.id == saleCustomer?.id ? "Remove" : "Select"} style={styles.selectBtnText} />
														</TouchableOpacity>
													</View>

													{/* <ZenDivider style={{ marginVertical: 20 }} /> */}

													{/* Account & Loyalty Stats */}
													{/* <View style={styles.statsRow}>
														<View style={styles.statItem}>
															<ZenIcon name='star' type='antdesign' size={14} color={theme?.goldenOrange} />
															<ZenText content="Points:" style={styles.statLabel} />
															<ZenText content={foundCustomer?.loyaltyPoints || "0"} style={styles.statValue} />
														</View>
														<View style={[styles.statItem, { borderLeftWidth: 1, borderLeftColor: theme?.softSlate, paddingLeft: 12 }]}>
															<ZenIcon name='wallet' type='antdesign' size={14} color={theme?.tealGreen} />
															<ZenText content="Credit:" style={styles.statLabel} />
															<ZenText content={CommonService.currencyStandard(currencySymbol, foundCustomer?.onAccountDetails?.balance?.toString() || "0")} style={styles.statValue} />
														</View>
													</View> */}
												</View>
											) : phone && !isSearching ? (
												/* --- NEW CUSTOMER FORM --- */
												<View>
													<View style={styles.newBadge}>
														<ZenIcon name='user-plus' type='feather' size={14} color={theme?.primary} />
														<ZenText content="New Customer" style={styles.newBadgeText} />
													</View>

													<ZenText content={'Full Name (Optional)'} style={styles.label} />

													<View style={{ paddingBottom: 10 }}>
														<CustomSearchBar
															placeholder="Enter customer name"
															search={customerName}
															setSearch={setCustomerName}
															toggleOffset={() => { }}
															backgroundColor={theme?.iceWhite}
															leftIcon={false}
															border={true}
															height={50}
															width={"100%"}
															leftComponent={<ZenIcon name='user' type='antdesign' size={18} color={theme?.grey} style={{ paddingRight: 5 }} />}
														/>
													</View>
													<TouchableOpacity style={styles.primaryButton} disabled={!phone?.length} onPress={createAndSelectCustomer}>
														{addCustomerLoader ?
															<ActivityIndicator color={theme?.light} size={'small'} /> :
															<ZenText content="Create & Select" style={styles.primaryButtonText} />}
													</TouchableOpacity>
												</View>
											) : null}
									</View>
								</View>
							</View>
						</SellscreenModal>

						<CustomModal visible={isVisible} transparent animationType="fade" onRequestClose={() => setIsVisible(false)}>
							<TouchableOpacity
								style={{
									flex: 1,
									backgroundColor: "rgba(0, 0, 0, 0.1)", // semi-transparent overlay
									justifyContent: width > height ? "flex-start" : "center",
									alignItems: width > height ? "flex-end" : "center",
									paddingTop: width > height ? popoverPos.top + 10 : 0,
									paddingRight: width > height ? 0 : 0,
								}}
								activeOpacity={1}
								onPress={() => setIsVisible(false)}
							>
								<Pressable onPress={() => { }} style={[styles.popoverCard, width > height ? { top: popoverPos.top, right: popoverPos.right, width: 380, maxHeight: height - popoverPos.top - 40 } : { width: "70%", top: "15%", alignSelf: "center", maxHeight: height * 0.7 }]}>
									{/* The Triangle Pointer */}
									{width > height && <View style={styles.arrowPointer} />}

									<View style={{ flexDirection: 'row', justifyContent: 'space-between', padding: 15, paddingBottom: 10 }}>
										<ZenText content="Customer" bold color='#4A90E2' size={theme.mediumFontSize} />
										<TouchableOpacity onPress={() => setIsVisible(false)}>
											<ZenIcon type='ionicon' name="close" size={22} color="#4A90E2" />
										</TouchableOpacity>
									</View>

									<ScrollView bounces={false} scrollEnabled nestedScrollEnabled showsVerticalScrollIndicator={false}>
										<View style={styles.profileSection}>
											<View style={styles.avatarLarge}>
												<ZenIcon type='material-community' name="account" size={35} color="#4A90E2" />
											</View>
											<View style={{ marginLeft: 16 }}>
												<ZenText content={captializeFirstLetter(saleCustomer?.name!)} bold color='#1A1C1E' size={theme.largerFontSize} />
												<ZenText content={'📞 +' + saleCustomer?.countryCode + " " + saleCustomer?.phone} color='#7C7C7C' size={theme.errorFontSize} style={styles.phoneSub} />
											</View>
										</View>

										{/* Stats Section with Vector Icons */}
										{/* <View style={styles.paddingH}>
											<StatRow name="star-outline" label="Loyalty Points" value={`${format(rawCustomerDetails?.balancePoints!)} pts`} color="#4A90E2" iconBg="#FFF8E6" iconColor="#FFB000" />
											<StatRow name="wallet-outline" label="On Account Balance" value={`${currencySymbol}${format(rawCustomerDetails?.accountLimitValue || 0)}`} color="#27AE60" iconBg="#E8F5E9" iconColor="#27AE60" />
											<StatRow name="shopping-outline" label="Total Orders" value="18" iconBg="#F3E5F5" iconColor="#9C27B0" />
										<StatRow name="fire" label="Total Spent" value={CommonService.currencyStandard(currencySymbol, "8560")} iconBg="#FFEBEE" iconColor="#F44336" />
										</View> */}

										<ZenDivider style={{ backgroundColor: "#F0F2F5", marginVertical: 5 }} />

										{/* Action Rows */}
										<View style={styles.paddingH}>
											{saleCustomer?.name !== "Guest customer" && <ActionRow
												icon="account-circle-outline"
												title="View Full Profile"
												sub="See orders, history and more"
												onPress={() => {
													setIsVisible(false);
													navigation.navigate("ViewCustomerDetails", { customerId: saleCustomer?.customerId! })
												}}
											/>}
											<ActionRow
												icon="account-switch-outline"
												title="Switch Customer"
												sub="Park current sale and select another"
												onPress={() => {
													if (cartProducts?.[locationId]?.length) {
														setSelectedOption("parkSale")
														setIsSwitchRemoveModalVisible(prev => ({ ...prev, parkSale: true }))
													} else {
														removeCustomerHandler()
														setIsVisible(false)
														setCustomerModal(true)
													}
												}}
											/>
											<ActionRow
												icon="account-remove-outline"
												title="Remove Customer"
												sub="Remove customer from this sale"
												isDestructive={true}
												onPress={() => {
													if (cartProducts?.[locationId]?.length) {
														setSelectedOption("removeCustomer")
														setIsSwitchRemoveModalVisible(prev => ({ ...prev, removeCustomer: true }))
													} else {
														setIsVisible(false)
														handleSelectCustomer()
													}
												}}
											/>
										</View>

										{!isPaymentScreen && cartProducts[locationId]?.length && isWrite && <View style={styles.footer}>
											<ZenText content="Park Current Sale" style={styles.footerTitle} bold color='#1A1C1E' size={theme.errorFontSize} />
											<TouchableOpacity style={styles.parkButton} onPress={() => {
												setParkSaleModalVisible(true)
												setTempNote(saleCustomer?.firstName + "'s Sale")
												setIsSwitchRemoveModalVisible(prev => ({ ...prev, parkSale: false }))
											}}>
												<View style={styles.parkIconBox}>
													<ZenIcon type='material-community' name="pause-circle-outline" size={20} color="#27AE60" />
												</View>
												<ZenText content="Park this sale and resume later" style={styles.parkText} color='#1A1C1E' size={theme.tinyFontSize} />
												<ZenIcon type='material-community' name="chevron-right" size={20} color="#CCC" />
											</TouchableOpacity>
										</View>}
									</ScrollView>
								</Pressable>
							</TouchableOpacity>
						</CustomModal>

						<SwitchRemoveCustomerModal
							mainText={`Current sale belongs to ${saleCustomer?.firstName}.`}
							subText="What would you like to do?"
							visible={isSwitchRemoveModalVisible?.parkSale}
							onClose={() => setIsSwitchRemoveModalVisible(prev => ({ ...prev, parkSale: false }))}
							onSuccess={onSuccess}
							radioGroup={{
								radioBtn1: {
									id: "parkSale",
									subText: "Park current sale and switch customer",
									title: "Current sale will be parked and you can select another customer."
								},
								radioBtn2: {
									id: "clearCartParkSale",
									subText: "Clear current cart",
									title: "All items will be removed from the cart."
								}
							}}
							selectedOption={selectedOption}
							setSelectedOption={setSelectedOption}
						/>
						<SwitchRemoveCustomerModal
							mainText={`Removing ${saleCustomer?.firstName} from this sale.`}
							subText='What would you like to do?'
							visible={isSwitchRemoveModalVisible?.removeCustomer}
							onClose={() => setIsSwitchRemoveModalVisible(prev => ({ ...prev, removeCustomer: false }))}
							isDestructive={true}
							radioGroup={{
								radioBtn1: {
									id: "removeCustomer",
									subText: "Keep cart items",
									title: "Keep current items in the cart."
								},
								radioBtn2: {
									id: "clearCartRemoveCustomer",
									subText: "Clear cart items",
									title: "All items will be removed from the cart."
								}
							}}
							onSuccess={onSuccess}
							selectedOption={selectedOption}
							setSelectedOption={setSelectedOption}
						/>

						<CustomModal keyboardView visible={parkSaleModalVisible} transparent animationType="fade" onRequestClose={() => setParkSaleModalVisible(false)}>
							<TouchableOpacity
								style={styles.modalOverlay}
								activeOpacity={1}
								onPress={() => Keyboard.dismiss()}
							>
								<Pressable style={StyleSheet.absoluteFill} onPress={() => setParkSaleModalVisible(false)} />
								<View style={[styles.modalContent, width < 600 && { width: "100%" }]}>
									<ZenText content={"Sale Notes"} style={styles.modalTitle} />
									<TextInput
										style={styles.inputBox}
										placeholder="Enter a sale notes or customer name"
										placeholderTextColor={theme?.grey}
										value={tempNote}
										onChangeText={setTempNote}
										multiline
										autoFocus
									/>
									<View style={styles.modalActions}>
										<TouchableOpacity onPress={() => setParkSaleModalVisible(false)} style={styles.cancelBtn}>
											<ZenText content={"Cancel"} style={styles.cancelText} />
										</TouchableOpacity>
										<TouchableOpacity onPress={handleSave} style={styles.saveBtn}>
											<ZenText content={"Save Sale"} style={styles.saveText} />
										</TouchableOpacity>
									</View>
								</View>

							</TouchableOpacity>
						</CustomModal>
					</>
				)
			}}
		</ThemeConsumer >
	);
};

const style = (theme: Theme) => StyleSheet.create({
	customerChip: {
		flexDirection: 'row',
		alignItems: 'center',
		paddingHorizontal: 12,
		paddingVertical: 5,
		borderRadius: 150,
		borderWidth: 1,
	},
	inactiveChip: {
		backgroundColor: theme?.iceWhite,
		borderColor: theme?.coolGray,
	},
	activeChip: {
		backgroundColor: theme?.tealPrimary,
		borderColor: theme?.lightPrimary,
		elevation: 1
	},
	iconCircle: {
		borderRadius: 20,
		padding: 7,
		justifyContent: 'center',
		alignItems: 'center',
		marginRight: 8,
	},
	inactiveCircle: {
		backgroundColor: theme.light,
		borderWidth: 1,
		borderColor: theme?.coolGray,
	},
	activeCircle: {
		backgroundColor: theme?.primary,
	},
	customerName: {
		fontSize: theme?.errorFontSize,
		fontFamily: theme?.bold,
	},
	textInactive: {
		color: theme?.disabledGray500,
	},
	textActive: {
		color: theme?.primary,
	},

	header: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		paddingBottom: 16,
		borderBottomWidth: 1,
		borderBottomColor: theme?.softSlate,
	},
	headerTitle: {
		fontSize: theme?.mediumFontSize,
		fontFamily: theme?.bold,
		color: theme?.lightDark,
	},

	inputWrapper: { gap: 6, marginBottom: 12 },
	label: { fontSize: theme?.errorFontSize, fontFamily: theme?.medium, color: theme?.disabledGray500, marginLeft: 4, paddingBottom: 5 },
	inputContainer: {
		flexDirection: 'row', alignItems: 'center', backgroundColor: theme?.iceWhite,
		borderWidth: 1.5, borderColor: theme?.softSlate, borderRadius: 14, paddingHorizontal: 12, height: 50, gap: 10
	},

	dynamicArea: { marginTop: 8 },

	/* Customer Card Styling */
	customerCard: { backgroundColor: theme?.light, borderWidth: 1, borderColor: theme?.coolGray, borderRadius: 18, padding: 16, gap: 12 },
	cardHeader: { flexDirection: 'row', alignItems: 'center', gap: 12 },
	avatar: { width: 44, height: 44, borderRadius: 22, backgroundColor: theme?.primary, justifyContent: 'center', alignItems: 'center' },
	avatarText: { color: theme?.light, fontSize: theme?.mediumFontSize, fontFamily: theme?.bold },
	mainInfo: { flex: 1 },
	customerPhone: { fontSize: theme?.errorFontSize, color: theme?.disabledGray500 },
	selectBtn: { backgroundColor: theme?.primary, paddingHorizontal: 14, paddingVertical: 8, borderRadius: 10 },
	selectBtnText: { color: theme?.light, fontSize: theme?.errorFontSize, fontFamily: theme?.bold },


	statsRow: { flexDirection: 'row', gap: 16, alignItems: 'center' },
	statItem: { flexDirection: 'row', alignItems: 'center', gap: 6 },
	statLabel: { fontSize: theme?.errorFontSize, color: theme?.draggerIcon, fontFamily: theme?.medium },
	statValue: { fontSize: theme?.errorFontSize, fontFamily: theme?.medium, color: theme?.lightDark },

	/* New Form Styling */
	newBadge: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: theme?.tealPrimary, paddingHorizontal: 10, paddingVertical: 4, borderRadius: 8, alignSelf: 'flex-start', marginBottom: 15 },
	newBadgeText: { fontSize: theme?.badgeFontSize, fontFamily: theme?.bold, color: theme?.primary, textTransform: 'uppercase' },
	primaryButton: { backgroundColor: theme?.primary, height: 50, borderRadius: 14, justifyContent: 'center', alignItems: 'center', marginTop: 10 },
	primaryButtonText: { color: theme?.light, fontSize: theme?.tinyFontSize, fontFamily: theme?.medium },

	customerBox: { flexDirection: 'row', alignItems: 'center', backgroundColor: theme?.iceWhite, padding: 10, borderRadius: 16, borderWidth: 1, borderColor: theme?.coolGray },
	custName: { fontFamily: theme?.bold, color: theme?.lightDark, fontSize: theme?.smallHeaderFontSize },
	custSub: { fontSize: theme?.miniFontSize, color: theme?.grey, fontFamily: theme?.medium },

	container: { flex: 1, padding: 20, alignItems: 'flex-end', backgroundColor: '#F5F7FA' },
	headerTrigger: {
		flexDirection: 'row',
		alignItems: 'center',
		backgroundColor: '#FFF',
		paddingHorizontal: 16,
		paddingVertical: 10,
		borderRadius: 14,
		borderWidth: 1.5,
		borderColor: '#DCE4FF',
		width: 280,
		// Matching the elevation/shadow in image_3bae5a.png
		shadowColor: '#4A90E2',
		shadowOffset: { width: 0, height: 4 },
		shadowOpacity: 0.1,
		shadowRadius: 8,
		elevation: 3,
	},
	avatarSmall: { width: 40, height: 40, borderRadius: 12, backgroundColor: '#EFF4FF', justifyContent: 'center', alignItems: 'center' },
	headerName: { fontWeight: '700', fontSize: 15, color: '#1A1C1E' },
	headerPhone: { fontSize: 11, color: '#7C7C7C' },


	popoverCard: {
		position: 'absolute',
		// ⬇ This is what enables ScrollView to actually scroll.
		// Without a maxHeight the card grows unboundedly, so ScrollView
		// never needs to scroll — it just expands with its content.
		maxHeight: '80%',
		backgroundColor: '#FFF',
		borderRadius: 20,
		overflow: 'hidden',
		shadowColor: '#000',
		shadowOffset: { width: 0, height: 15 },
		shadowOpacity: 0.2,
		shadowRadius: 20,
		elevation: 15,
		paddingBottom: 10,
	},
	arrowPointer: {
		position: 'absolute',
		top: -8,
		right: 40,
		width: 20,
		height: 20,
		backgroundColor: '#FFF',
		transform: [{ rotate: '45deg' }],
	},

	profileSection: { flexDirection: 'row', alignItems: 'center', padding: 15 },
	avatarLarge: { width: 70, height: 70, borderRadius: 42, backgroundColor: '#EFF4FF', justifyContent: 'center', alignItems: 'center' },
	goldBadge: { backgroundColor: '#FFF4DE', paddingHorizontal: 10, paddingVertical: 4, borderRadius: 20, marginLeft: 10 },
	goldText: { color: '#FF9500', fontSize: 11, fontWeight: '700' },
	phoneSub: { marginTop: 4 },

	paddingH: { paddingHorizontal: 15 },
	statRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginVertical: 10 },
	statLabelContainer: { flexDirection: 'row', alignItems: 'center' },
	iconBox: { width: 38, height: 38, borderRadius: 12, justifyContent: 'center', alignItems: 'center' },

	actionRow: { flexDirection: 'row', alignItems: 'center', marginBottom: 22 },
	actionSub: { marginTop: 3 },

	footer: { padding: 15, borderTopWidth: 1, borderTopColor: '#F0F2F5' },
	footerTitle: { marginBottom: 15, },
	parkButton: { flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderColor: '#E0E7FF', borderRadius: 16, padding: 14 },
	parkIconBox: { width: 34, height: 34, borderRadius: 10, backgroundColor: '#E8F5E9', justifyContent: 'center', alignItems: 'center', marginRight: 12 },
	parkText: { flex: 1 },
	cancelText: { color: theme?.grey, fontFamily: theme?.medium, fontSize: theme?.errorFontSize },
	saveBtn: { backgroundColor: theme?.primary, paddingHorizontal: 20, paddingVertical: 10, borderRadius: 8 },
	saveText: { color: theme?.light, fontFamily: theme?.bold, fontSize: theme?.errorFontSize },
	cancelBtn: { padding: 10 },
	modalOverlay: {
		flex: 1,
		backgroundColor: theme?.modalBg100,
		justifyContent: 'center',
		alignItems: "center",
		padding: 20,
	},
	modalContent: {
		width: "60%",
		backgroundColor: theme?.light,
		borderRadius: 16,
		padding: 20,
	},
	modalTitle: { fontSize: theme?.smallerFontSize, fontFamily: theme?.bold, marginBottom: 12 },
	inputBox: {
		backgroundColor: theme?.softSlate,
		borderRadius: 8,
		padding: 12,
		height: 100,
		textAlignVertical: 'top',
		fontSize: theme?.smallHeaderFontSize,
		color: theme?.lightDark,
		fontFamily: theme?.medium,
		marginBottom: 16,
	},
	modalActions: { flexDirection: 'row', justifyContent: 'flex-end', gap: 12 },

});

export default CustomerModal;